import { Component } from '@theme/component';

/**
 * Announcement banner: fade (stacked) or slide (horizontal scroll in viewport).
 * Slide mode uses scrollLeft on `.announcement-bar__slides` — not CSS transform —
 * so flex + nested content cannot break percentage-based motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Keeps scroll position aligned with the current index when the viewport resizes.
   * @type {ResizeObserver|undefined}
   */
  #slideResizeObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
    this.#bindSlideResizeObserver();
    requestAnimationFrame(() => {
      this.#applySlideScrollFromIndex(this.#getRelativeIndex());
    });
  }

  disconnectedCallback() {
    this.suspend();
    this.#slideResizeObserver?.disconnect();
    this.#slideResizeObserver = undefined;
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const relativeIndex = this.#getRelativeIndex();

    for (const [index, slide] of (this.refs.slides ?? []).entries()) {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    }

    this.#applySlideScrollFromIndex(relativeIndex);
  }

  /**
   * @returns {number}
   */
  #getRelativeIndex() {
    const slides = this.refs.slides ?? [];
    const len = slides.length;
    if (len === 0) return 0;

    let relativeIndex = this.#current % len;
    if (relativeIndex < 0) {
      relativeIndex += len;
    }

    return relativeIndex;
  }

  /**
   * @param {number} relativeIndex
   */
  #applySlideScrollFromIndex(relativeIndex) {
    const viewport = this.querySelector('.announcement-bar__slides');
    const isSlideMode =
      this.dataset.announcementTransition === 'slide' &&
      viewport?.classList.contains('announcement-bar__slides--slide');

    if (isSlideMode && viewport) {
      const w = viewport.clientWidth;
      if (w > 0) {
        const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        viewport.scrollTo({
          left: relativeIndex * w,
          behavior: instant ? 'auto' : 'smooth',
        });
      }
    }
  }

  #bindSlideResizeObserver() {
    const viewport = this.querySelector('.announcement-bar__slides');
    if (!viewport?.classList.contains('announcement-bar__slides--slide')) return;

    this.#slideResizeObserver = new ResizeObserver(() => {
      const idx = this.#getRelativeIndex();
      const w = viewport.clientWidth;
      if (w > 0) {
        viewport.scrollTo({ left: idx * w, behavior: 'auto' });
      }
    });
    this.#slideResizeObserver.observe(viewport);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
